import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import ProductCard from '~/components/ProductCard/ProductCard';
import ProductCardSkeletonM from '~/components/ProductCardV3/skeletons/ProductCardSkeletonM';
import { useGlobal } from '~/hooks/useGlobal';
import { catalogStore } from '~/stores';

import { ProductRecommendationsProps } from './interfaces';
import styles from './ProductRecommendationsGridXL.module.scss';

/**
 * Grid-based recommendations with XL cards (4 per row on desktop).
 * No carousel; uses regular grid layout with fixed gap.
 */
const ProductRecommendationsGridXL = ({
  source,
  title,
  className,
  isLoading,
}: ProductRecommendationsProps) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();

  const products = catalogStore.productRecommendations[source];

  const renderSkeletons = () => (
    <div className={styles.grid}>
      {Array.from({ length: isMobile ? 4 : 8 }, (_, i) => (
        <ProductCardSkeletonM
          className={styles.product}
          key={`skeleton-${i}`}
        />
      ))}
    </div>
  );

  if (catalogStore.productRecommendationsMeta.isLoading || isLoading) {
    return (
      <div className={classes([styles.root, className])}>
        {title && (
          <h2 className={styles.title}>
            <Skeleton className={styles.titleSkeleton} />
          </h2>
        )}
        {renderSkeletons()}
      </div>
    );
  }

  if (!products || !products.data.length) {
    return null;
  }

  const items = products.data.map((product) => (
    <ProductCard
      key={product.id}
      product={product}
      className={styles.product}
      variant="xl"
    />
  ));

  return (
    <div className={classes([styles.root, className])}>
      {title && <h2 className={styles.title}>{t(title)}</h2>}
      <div className={styles.grid}>{items}</div>
    </div>
  );
};

export default observer(ProductRecommendationsGridXL);
